import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import QuizApi from "../../api/quizApi";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import Input from "../../components/Input/Input";
import Loader from "../../components/Loader/Loader";
import MultipleSelect from "../../components/MultipleSelect/MultipleSelect";
import StandartButton from "../../components/StandartButton/StandartButton";
import { Quiz, QuizType, TypeQuizFilters } from "../../types/Quiz";
import Notify, { SuccesNotify } from "../../utils/toaster";
import { assertQuizChapters } from "../../utils/quizChapters";
import SystemQuizView from "./SystemQuizView";
import s from "./SystemQuizzes.module.css";
import SystemQuizzesTable from "./SystemQuizzesTable";
import {
  getQuizDisplayTitle,
  isFeedbackQuizType,
  quizTypeOptions,
} from "./systemQuizModel";

type QuizLoadResponse = {
  status: boolean;
  message?: string;
  quiz?: Quiz | null;
};

const getDeleteConfirmationTitle = (quiz: Quiz | null) => {
  if (quiz?.isSystemTemplate && isFeedbackQuizType(quiz.type ?? "")) {
    return `Are you sure you want to delete reusable Feedback template "${getQuizDisplayTitle(
      quiz,
    )}"?`;
  }

  return "Are you sure you want to delete this System Quiz?";
};

const SystemQuizzes = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedQuizId = searchParams.get("selectedQuiz");
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [selectedQuiz, setSelectedQuiz] = useState<Quiz | null>(null);
  const [quizToDelete, setQuizToDelete] = useState<Quiz | null>(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [selectedQuizLoading, setSelectedQuizLoading] = useState(false);
  const [selectedViewLanguage, setSelectedViewLanguage] = useState("");
  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
    text: string;
    type: QuizType[];
  }>({
    page: 1,
    limit: 10,
    text: "",
    type: [],
  });
  const [total, setTotal] = useState(0);
  const [listError, setListError] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) return;
    let ignore = false;
    const fetchQuizzes = async () => {
      setListLoading(true);
      setListError("");
      try {
        const quizFilters: TypeQuizFilters = {
          isSystemTemplate: true,
          ...(filters.text.trim() ? { text: filters.text.trim() } : {}),
          ...(filters.type.length ? { type: filters.type } : {}),
        };
        const response = await QuizApi.getQuizes(token, {
          page: filters.page,
          limit: filters.limit,
          filters: quizFilters,
        });
        if (ignore) return;
        if (response.status) {
          (response.quizes ?? []).forEach((quiz) => assertQuizChapters(quiz.quiz));
          setQuizzes(response.quizes ?? []);
          setTotal(response.total ?? 0);
          return;
        }
        throw new Error(response.message || "Unable to load System Quizzes.");
      } catch (error) {
        if (!ignore) {
          setQuizzes([]);
          setTotal(0);
          setListError(
            error instanceof Error ? error.message : "Unable to load System Quizzes.",
          );
        }
      } finally {
        if (!ignore) setListLoading(false);
      }
    };
    fetchQuizzes();
    return () => {
      ignore = true;
    };
  }, [filters, token, refreshKey]);

  useEffect(() => {
    let ignore = false;

    const fetchSelectedQuiz = async () => {
      if (!selectedQuizId) {
        setSelectedQuizLoading(false);
        setSelectedQuiz(null);
        setSelectedViewLanguage("");
        return;
      }

      if (!token) return;

      const existingQuiz = quizzes.find((quiz) => quiz._id === selectedQuizId);
      if (existingQuiz) {
        setSelectedQuizLoading(false);
        setSelectedQuiz(existingQuiz);
        setSelectedViewLanguage(
          (prev) => prev || existingQuiz.title?.[0]?.language || "",
        );
        return;
      }

      setSelectedQuizLoading(true);
      try {
        const response = (await QuizApi.getQuizById(
          token,
          selectedQuizId,
        )) as QuizLoadResponse;

        if (ignore) return;

        if (response.status && response.quiz) {
          setSelectedQuiz(response.quiz);
          setSelectedViewLanguage(
            response.quiz.title?.[0]?.language ||
              response.quiz.description?.[0]?.language ||
              "",
          );
          return;
        }

        Notify(response.message ?? "Unable to load System Quiz");
        setSearchParams({});
      } catch (error) {
        if (ignore) return;
        console.error("Error loading System Quiz:", error);
        Notify("Unable to load System Quiz");
        setSearchParams({});
      } finally {
        if (!ignore) setSelectedQuizLoading(false);
      }
    };

    fetchSelectedQuiz();

    return () => {
      ignore = true;
    };
  }, [selectedQuizId, token, quizzes, setSearchParams]);

  const deleteQuiz = async () => {
    if (!token || !quizToDelete) return;

    const response = await QuizApi.deleteQuiz(token, quizToDelete._id);
    if (response.status) {
      SuccesNotify("System Quiz deleted");
      setDeleteConfirmVisible(false);
      if (selectedQuizId === quizToDelete._id) {
        setSearchParams({});
        setSelectedQuiz(null);
      }
      setQuizToDelete(null);
      setSelectedQuiz(null);
      setRefreshKey((previous) => previous + 1);
      return;
    }

    if (response.message) Notify(response.message);
  };

  return (
    <div className={s.container}>
      <header className={s.header}>
        <div>
          <h1 className={s.title}>System Quizzes</h1>
          <p className={s.subtitle}>
            Manage reusable quiz definitions for Talzi products.
          </p>
        </div>
        <StandartButton
          buttonTitle="Create System Quiz"
          action={() => navigate("/system-quizzes/new")}
        />
      </header>

      {selectedQuizId ? (
        selectedQuizLoading ? (
          <div className={s.state}>
            <Loader size={64} />
            <p>Loading System Quiz...</p>
          </div>
        ) : selectedQuiz ? (
          <SystemQuizView
            quiz={selectedQuiz}
            selectedLanguage={selectedViewLanguage}
            onLanguageChange={setSelectedViewLanguage}
            onBack={() => setSearchParams({})}
            onEdit={() => navigate(`/system-quizzes/${selectedQuiz._id}/edit`)}
            onDelete={() => {
              setQuizToDelete(selectedQuiz);
              setDeleteConfirmVisible(true);
            }}
          />
        ) : (
          <div className={s.emptyState}>System Quiz not found</div>
        )
      ) : (
        <>
          <div className={s.filters}>
            <div className={s.searchBlock}>
              <Input
                inputValue={filters.text}
                onChangeInput={(value) =>
                  setFilters((prev) => ({ ...prev, text: value, page: 1 }))
                }
                placeholder="Search by title..."
                isSearchinput
              />
            </div>
            <MultipleSelect
              data={quizTypeOptions}
              multiplie
              checkBox
              chip
              resetBtn
              lable="Quiz Type"
              value={filters.type}
              setValue={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  type: value as QuizType[],
                  page: 1,
                }))
              }
              width="320px"
              disableSortData
            />
          </div>

          <div className={s.tableBlock}>
            {listLoading ? (
              <div className={s.state}>
                <Loader size={64} />
                <p>Loading System Quizzes...</p>
              </div>
            ) : listError ? (
              <div role="alert" className={s.emptyState}>
                {listError}
              </div>
            ) : quizzes.length ? (
              <SystemQuizzesTable
                rows={quizzes}
                count={total}
                page={filters.page}
                rowsPerPage={filters.limit}
                setPage={(page) => setFilters((prev) => ({ ...prev, page }))}
                setRowsPerPage={(limit) =>
                  setFilters((prev) => ({ ...prev, limit, page: 1 }))
                }
                onSelectQuiz={(quiz) =>
                  setSearchParams({ selectedQuiz: quiz._id })
                }
                onEditQuiz={(quiz) =>
                  navigate(`/system-quizzes/${quiz._id}/edit`)
                }
                onDeleteQuiz={(quiz) => {
                  setQuizToDelete(quiz);
                  setDeleteConfirmVisible(true);
                }}
              />
            ) : (
              <div className={s.emptyState}>No System Quizzes</div>
            )}
          </div>
        </>
      )}

      <ConfirmationModal
        confirmFunc={deleteQuiz}
        confirmTitle={getDeleteConfirmationTitle(quizToDelete)}
        modalVisible={deleteConfirmVisible}
        setModalVisible={(value) => {
          setDeleteConfirmVisible(value);
          if (!value) setQuizToDelete(null);
        }}
      />
    </div>
  );
};

export default SystemQuizzes;
